import { useApiCall } from '@/hooks/useApiCall';
import { FunctionComponent, useState } from 'react';
import { locationService } from '../../api/locations/location.service';
import { AssetTypeTotalModel } from '@/modules/asset-types';
import { useParams } from 'react-router';
import { GuestLocationBalanceGrid } from './GuestLocationBalanceGrid';
import LocationHierarchyToggleButton from '../LocationHierarchyToggleButton';
import { DisplayMode } from '@/types/display-mode.enum';
import { useTranslation } from '@/lib';
import { GridApi } from 'ag-grid-community';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import dayjs from 'dayjs';
import LocationBalanceBuildupDrawer from '@/modules/balances/components/LocationBalanceBuildupDrawer';

export const GuestLocationBalanceTab: FunctionComponent = () => {
  const { locationId } = useParams();
  if (!locationId) {
    return;
  }

  const { t } = useTranslation();

  const locationIdNumber = Number.parseInt(locationId);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.INDIVIDUAL);
  const [isBuildupDrawerOpen, setIsBuildupDrawerOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeTotalModel | null>(null);
  const { data, isLoading, isError } = useApiCall<AssetTypeTotalModel[]>(() => locationService.getBalance({ locationId: locationIdNumber }));
  const { exportToExcel } = useExportToExcel();

  const handleBuildupClick = (assetType: AssetTypeTotalModel) => {
    setSelectedAssetType(assetType);
    setIsBuildupDrawerOpen(true);
  };

  const handleBuildupDrawerClose = () => {
    setIsBuildupDrawerOpen(false);
    setSelectedAssetType(null);
  };

  const onExportToExcelClicked = async (_gridApi: GridApi<AssetTypeTotalModel> | undefined) => {
    if (_gridApi) {
      const balanceData = await locationService.getBalance({ locationId: locationIdNumber });
      const payload = balanceData.payload;
      if (payload && payload.length > 0) {
        console.log('Preparing to export data:', payload);
        exportToExcel(
          payload.map((assetType) => ({
            assetTypeCode: assetType.code,
            assetTypeName: assetType.name,
            balance: displayMode === DisplayMode.HIERARCHICAL ? assetType.hierarchicalBalance : assetType.individualBalance,
          })),
          `location_balance_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`,
        );
      }
    }
  };

  return (
    <div className="flex flex-col h-full flex-1 grow gap-x-4  max-w-[1920px] ">
      <div className="flex items-center  justify-between w-full">
        <div className="text-md  font-bold">{t('balance_per_asset_type')}</div>
        <div className="text-xs flex items-center scale-75 origin-right">
          <LocationHierarchyToggleButton displayMode={displayMode} onChange={setDisplayMode} reverseOrder />
        </div>
      </div>

      <GuestLocationBalanceGrid
        data={data}
        isError={isError}
        isLoading={isLoading}
        balanceDisplayMode={displayMode}
        locationId={locationIdNumber}
        onExportToExcelClicked={onExportToExcelClicked}
        onBuildupClick={handleBuildupClick}
      ></GuestLocationBalanceGrid>

      {selectedAssetType && (
        <LocationBalanceBuildupDrawer
          locationId={locationIdNumber}
          assetTypeId={selectedAssetType.id}
          assetTypeName={selectedAssetType.name}
          assetTypeCode={selectedAssetType.code}
          isOpen={isBuildupDrawerOpen}
          onClose={handleBuildupDrawerClose}
          displayMode={displayMode}
          isGuestView={true}
        />
      )}
    </div>
  );
};
